/**
 * SharePoint Documents Page
 * Browse and search SharePoint documents with real Microsoft Graph data.
 * Follows the Projects page design pattern.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { ArrowLeft, ChevronRight, Download, ExternalLink, FileText, Folder, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { PageBody, PageEmptyState, PageHeader, PageHero, PageToolbar } from '../components/layout';
import { useAuth } from '../contexts/authContext';
import { useFeatureFlag, useFeatureFlagsReady } from '../contexts/featureFlagContext';
import { useSharePointAuth } from '../contexts/sharePointAuthContext';
import { useSharePoint } from '../hooks/useSharePoint';
import { useLayoutStore } from '../lib/stores/LayoutStore';
const formatFileSize = (bytes)=>{
    const sizes = [
        'Bytes',
        'KB',
        'MB',
        'GB'
    ];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / 1024 ** i * 100) / 100} ${sizes[i]}`;
};
const formatDate = (dateString)=>{
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    });
};
export default function SharePointDocuments() {
    const { user } = useAuth();
    const { isSharePointConnected, connectSharePoint } = useSharePointAuth();
    const { loading, error, clearError, getUserSiteAccess, listSiteDrives, listDriveItems, searchDocuments, downloadDocument } = useSharePoint();
    // URL-backed navigation — query params drive what's displayed so the browser
    // back/forward buttons work naturally.
    //   /sharepoint-documents                                  → sites list
    //   ?site=<id>                                             → drives for that site
    //   ?site=<id>&drive=<id>                                  → drive root items
    //   ?site=<id>&drive=<id>&path=<folderId>,<folderId>,...   → nested folder
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedSite = searchParams.get('site');
    const selectedDrive = searchParams.get('drive');
    const pathParam = searchParams.get('path');
    const folderPath = useMemo(()=>pathParam ? pathParam.split(',').filter(Boolean) : [], [
        pathParam
    ]);
    const currentFolderId = folderPath[folderPath.length - 1];
    // Cached data keyed by URL position.
    const [sites, setSites] = useState([]);
    const [drives, setDrives] = useState([]);
    const [items, setItems] = useState([]);
    const [searchResults, setSearchResults] = useState(null);
    // Accumulates folder names as the user navigates in — used to render
    // breadcrumbs. On direct-URL load we fall back to the entity's ID for any
    // folder we haven't visited yet.
    const [folderNameMap, setFolderNameMap] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [sitesLoading, setSitesLoading] = useState(false);
    // Specra redesign: inline PageHero replaces the legacy top-bar PageHeader.
    const flagsReady = useFeatureFlagsReady();
    const redesignEnabled = useFeatureFlag('specra_redesign_sharepoint');
    const redesign = flagsReady && redesignEnabled;
    // Hide breadcrumbs on root page; also hide the global header when the
    // redesign renders its own inline hero.
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    const setShowHeader = useLayoutStore((state)=>state.setShowHeader);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        if (redesign) setShowHeader(false);
        return ()=>{
            setShowBreadcrumbs(true);
            if (redesign) setShowHeader(true);
        };
    }, [
        setShowBreadcrumbs,
        setShowHeader,
        redesign
    ]);
    // Load sites once per connected session — they drive both the root listing
    // and breadcrumb name lookups when a deep URL is loaded.
    useEffect(()=>{
        if (!isSharePointConnected || !user?.id || !user?.tenant_id) return;
        const loadSites = async ()=>{
            setSitesLoading(true);
            const access = await getUserSiteAccess(user.id, user.tenant_id || '');
            if (access?.sites) {
                setSites(access.sites);
            }
            setSitesLoading(false);
        };
        loadSites();
    }, [
        isSharePointConnected,
        user,
        getUserSiteAccess
    ]);
    // Load drives when the selected site changes (via URL or click).
    useEffect(()=>{
        if (!selectedSite) {
            setDrives([]);
            return;
        }
        let cancelled = false;
        (async ()=>{
            const result = await listSiteDrives(selectedSite);
            if (!cancelled && result) setDrives(result);
        })();
        return ()=>{
            cancelled = true;
        };
    }, [
        selectedSite,
        listSiteDrives
    ]);
    // Load items when drive or folder path changes.
    useEffect(()=>{
        if (!selectedSite || !selectedDrive) {
            setItems([]);
            return;
        }
        let cancelled = false;
        (async ()=>{
            const result = await listDriveItems(selectedSite, selectedDrive, currentFolderId);
            if (!cancelled && result?.items) setItems(result.items);
        })();
        return ()=>{
            cancelled = true;
        };
    }, [
        selectedSite,
        selectedDrive,
        currentFolderId,
        listDriveItems
    ]);
    const handleSiteClick = useCallback((site)=>{
        clearError();
        setSearchResults(null);
        setFolderNameMap((prev)=>({
                ...prev,
                [site.site_id]: site.site_name
            }));
        setSearchParams({
            site: site.site_id
        });
    }, [
        clearError,
        setSearchParams
    ]);
    const handleDriveClick = useCallback((drive)=>{
        if (!selectedSite) return;
        clearError();
        setSearchResults(null);
        setFolderNameMap((prev)=>({
                ...prev,
                [drive.id]: drive.name
            }));
        setSearchParams({
            site: selectedSite,
            drive: drive.id
        });
    }, [
        selectedSite,
        clearError,
        setSearchParams
    ]);
    const handleFolderClick = useCallback((folder)=>{
        if (!selectedSite || !selectedDrive) return;
        clearError();
        setSearchResults(null);
        setFolderNameMap((prev)=>({
                ...prev,
                [folder.id]: folder.name
            }));
        const newPath = [
            ...folderPath,
            folder.id
        ].join(',');
        setSearchParams({
            site: selectedSite,
            drive: selectedDrive,
            path: newPath
        });
    }, [
        selectedSite,
        selectedDrive,
        folderPath,
        clearError,
        setSearchParams
    ]);
    // Breadcrumbs reflect the URL: [Sites, site, drive, ...folders].
    const breadcrumbs = useMemo(()=>{
        const crumbs = [
            'Sites'
        ];
        if (selectedSite) {
            const siteName = sites.find((s)=>s.site_id === selectedSite)?.site_name;
            crumbs.push(folderNameMap[selectedSite] ?? siteName ?? 'Site');
        }
        if (selectedDrive) {
            const driveName = drives.find((d)=>d.id === selectedDrive)?.name;
            crumbs.push(folderNameMap[selectedDrive] ?? driveName ?? 'Drive');
        }
        for (const folderId of folderPath){
            crumbs.push(folderNameMap[folderId] ?? 'Folder');
        }
        return crumbs;
    }, [
        selectedSite,
        selectedDrive,
        folderPath,
        folderNameMap,
        sites,
        drives
    ]);
    const handleBreadcrumbClick = useCallback((index)=>{
        clearError();
        setSearchResults(null);
        // index 0 = Sites root; 1 = site (drives view); 2 = drive (items root);
        // 3+ = nested folders.
        if (index === 0) {
            setSearchParams({});
            return;
        }
        if (index === 1 && selectedSite) {
            setSearchParams({
                site: selectedSite
            });
            return;
        }
        if (index === 2 && selectedSite && selectedDrive) {
            setSearchParams({
                site: selectedSite,
                drive: selectedDrive
            });
            return;
        }
        if (selectedSite && selectedDrive) {
            const newPath = folderPath.slice(0, index - 2).join(',');
            if (newPath) {
                setSearchParams({
                    site: selectedSite,
                    drive: selectedDrive,
                    path: newPath
                });
            } else {
                setSearchParams({
                    site: selectedSite,
                    drive: selectedDrive
                });
            }
        }
    }, [
        selectedSite,
        selectedDrive,
        folderPath,
        clearError,
        setSearchParams
    ]);
    const handleSearch = useCallback(async ()=>{
        if (!searchQuery.trim()) {
            setSearchResults(null);
            return;
        }
        clearError();
        setIsSearching(true);
        const result = await searchDocuments(searchQuery);
        if (result) {
            setSearchResults(result.hits);
        }
        setIsSearching(false);
    }, [
        searchQuery,
        searchDocuments,
        clearError
    ]);
    const handleSearchKeyDown = useCallback((e)=>{
        if (e.key === 'Enter') handleSearch();
    }, [
        handleSearch
    ]);
    const handleClearSearch = useCallback(()=>{
        setSearchQuery('');
        setSearchResults(null);
    }, []);
    const handleDownload = useCallback(async (item)=>{
        const siteId = 'site_id' in item ? item.site_id : '';
        const driveId = 'drive_id' in item ? item.drive_id : '';
        const itemId = 'item_id' in item ? item.item_id : item.id;
        await downloadDocument(siteId, driveId, itemId);
    }, [
        downloadDocument
    ]);
    if (!isSharePointConnected) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx(Helmet, {
                    children: /*#__PURE__*/ _jsx("title", {
                        children: "SharePoint Documents - GovCon AI"
                    })
                }),
                redesign ? null : /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "SharePoint Documents"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Connect SharePoint to browse and search your documents"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs(PageBody, {
                    children: [
                        redesign && /*#__PURE__*/ _jsx(PageHero, {
                            title: "SharePoint Documents",
                            subtitle: "Connect SharePoint to browse and search your documents"
                        }),
                        /*#__PURE__*/ _jsx(PageEmptyState, {
                            icon: /*#__PURE__*/ _jsx(FileText, {}),
                            title: "SharePoint not connected",
                            description: "Connect your SharePoint account to browse documents.",
                            size: "sm",
                            action: {
                                label: 'Connect SharePoint',
                                onClick: ()=>{
                                    void connectSharePoint();
                                }
                            }
                        })
                    ]
                })
            ]
        });
    }
    const isAtSiteLevel = !selectedSite;
    const isAtDriveLevel = selectedSite && !selectedDrive;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs(Helmet, {
                children: [
                    /*#__PURE__*/ _jsx("title", {
                        children: "SharePoint Documents - GovCon AI"
                    }),
                    /*#__PURE__*/ _jsx("meta", {
                        name: "description",
                        content: "Browse SharePoint documents with ACL filtering"
                    })
                ]
            }),
            redesign ? null : /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-xl font-semibold",
                            children: "SharePoint Documents"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Browse and search your SharePoint documents"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    redesign && /*#__PURE__*/ _jsx(PageHero, {
                        title: "SharePoint Documents",
                        subtitle: "Browse and search your SharePoint documents"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center gap-1 mb-4 text-sm",
                        children: breadcrumbs.map((crumb, i)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-1",
                                children: [
                                    i > 0 && /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "w-3 h-3 text-gray-400"
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        onClick: ()=>handleBreadcrumbClick(i),
                                        className: `hover:text-blue-600 transition-colors ${i === breadcrumbs.length - 1 ? 'text-gray-900 font-medium' : 'text-gray-500 hover:underline'}`,
                                        disabled: i === breadcrumbs.length - 1,
                                        children: crumb
                                    })
                                ]
                            }, i))
                    }),
                    /*#__PURE__*/ _jsx(PageToolbar, {
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2 flex-1",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "relative flex-1 max-w-md",
                                    children: [
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "text",
                                            value: searchQuery,
                                            onChange: (e)=>setSearchQuery(e.target.value),
                                            onKeyDown: handleSearchKeyDown,
                                            placeholder: "Search all documents...",
                                            className: "w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        }),
                                        /*#__PURE__*/ _jsx(Search, {
                                            className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "outline",
                                    onClick: handleSearch,
                                    disabled: isSearching || loading,
                                    children: "Search"
                                }),
                                searchResults !== null && /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    onClick: handleClearSearch,
                                    children: [
                                        /*#__PURE__*/ _jsx(ArrowLeft, {
                                            className: "w-4 h-4 mr-1"
                                        }),
                                        "Back to browsing"
                                    ]
                                })
                            ]
                        })
                    }),
                    error && /*#__PURE__*/ _jsx("div", {
                        className: "mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800",
                        children: error
                    }),
                    searchResults !== null ? /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("h3", {
                                className: "text-sm font-medium text-gray-700 mb-3",
                                children: [
                                    searchResults.length,
                                    " result",
                                    searchResults.length !== 1 ? 's' : '',
                                    ' for "',
                                    searchQuery,
                                    '"'
                                ]
                            }),
                            searchResults.length === 0 ? /*#__PURE__*/ _jsx(PageEmptyState, {
                                icon: /*#__PURE__*/ _jsx(Search, {}),
                                title: "No results found",
                                description: "Try adjusting your search query",
                                size: "sm"
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "overflow-x-auto",
                                    children: /*#__PURE__*/ _jsxs("table", {
                                        className: "min-w-full divide-y divide-gray-200",
                                        children: [
                                            /*#__PURE__*/ _jsx("thead", {
                                                className: "bg-gray-50",
                                                children: /*#__PURE__*/ _jsxs("tr", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("th", {
                                                            className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                            children: "Document"
                                                        }),
                                                        /*#__PURE__*/ _jsx("th", {
                                                            className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                            children: "Modified"
                                                        }),
                                                        /*#__PURE__*/ _jsx("th", {
                                                            className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                            children: "Size"
                                                        }),
                                                        /*#__PURE__*/ _jsx("th", {
                                                            className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                            children: "Actions"
                                                        })
                                                    ]
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("tbody", {
                                                className: "bg-white divide-y divide-gray-200",
                                                children: searchResults.map((hit)=>/*#__PURE__*/ _jsxs("tr", {
                                                        className: "hover:bg-gray-50",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("td", {
                                                                className: "px-6 py-4",
                                                                children: /*#__PURE__*/ _jsxs("div", {
                                                                    className: "flex items-center",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(FileText, {
                                                                            className: "w-5 h-5 text-blue-500 flex-shrink-0"
                                                                        }),
                                                                        /*#__PURE__*/ _jsxs("div", {
                                                                            className: "ml-3",
                                                                            children: [
                                                                                /*#__PURE__*/ _jsx("div", {
                                                                                    className: "text-sm font-medium text-gray-900",
                                                                                    children: hit.name
                                                                                }),
                                                                                hit.summary && /*#__PURE__*/ _jsx("div", {
                                                                                    className: "text-xs text-gray-500 mt-0.5 line-clamp-2",
                                                                                    children: hit.summary
                                                                                })
                                                                            ]
                                                                        })
                                                                    ]
                                                                })
                                                            }),
                                                            /*#__PURE__*/ _jsxs("td", {
                                                                className: "px-6 py-4 whitespace-nowrap",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx("div", {
                                                                        className: "text-sm text-gray-900",
                                                                        children: formatDate(hit.modified_at)
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        className: "text-xs text-gray-500",
                                                                        children: [
                                                                            "by ",
                                                                            hit.last_modified_by
                                                                        ]
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsx("td", {
                                                                className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500",
                                                                children: formatFileSize(hit.size)
                                                            }),
                                                            /*#__PURE__*/ _jsx("td", {
                                                                className: "px-6 py-4 whitespace-nowrap text-right text-sm font-medium",
                                                                children: /*#__PURE__*/ _jsxs("div", {
                                                                    className: "flex items-center justify-end space-x-2",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("button", {
                                                                            onClick: ()=>window.open(hit.web_url, '_blank'),
                                                                            className: "text-indigo-600 hover:text-indigo-900 p-1",
                                                                            title: "Open in SharePoint",
                                                                            children: /*#__PURE__*/ _jsx(ExternalLink, {
                                                                                className: "w-4 h-4"
                                                                            })
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("button", {
                                                                            onClick: ()=>handleDownload(hit),
                                                                            className: "text-gray-600 hover:text-gray-900 p-1",
                                                                            title: "Download",
                                                                            children: /*#__PURE__*/ _jsx(Download, {
                                                                                className: "w-4 h-4"
                                                                            })
                                                                        })
                                                                    ]
                                                                })
                                                            })
                                                        ]
                                                    }, hit.hit_id))
                                            })
                                        ]
                                    })
                                })
                            })
                        ]
                    }) : isAtSiteLevel ? /* Sites List */ /*#__PURE__*/ _jsx("div", {
                        children: sitesLoading || loading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center py-12 text-gray-500",
                            children: "Loading sites..."
                        }) : sites.length === 0 ? /*#__PURE__*/ _jsx(PageEmptyState, {
                            icon: /*#__PURE__*/ _jsx(Folder, {}),
                            title: "No SharePoint sites found",
                            description: "Your account doesn't have access to any SharePoint sites.",
                            size: "sm"
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
                            children: sites.map((site)=>/*#__PURE__*/ _jsx("div", {
                                    onClick: ()=>handleSiteClick(site),
                                    className: "bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-blue-300 cursor-pointer transition-all",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsx(Folder, {
                                                className: "w-8 h-8 text-blue-500 flex-shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-0",
                                                children: [
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        className: "text-sm font-medium text-gray-900 truncate",
                                                        children: site.site_name
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-xs text-gray-500 truncate",
                                                        children: site.site_url
                                                    }),
                                                    site.permission_level && /*#__PURE__*/ _jsx("span", {
                                                        className: "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1",
                                                        children: site.permission_level
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                }, site.site_id))
                        })
                    }) : isAtDriveLevel ? /* Drives List */ /*#__PURE__*/ _jsx("div", {
                        children: loading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center py-12 text-gray-500",
                            children: "Loading drives..."
                        }) : drives.length === 0 ? /*#__PURE__*/ _jsx(PageEmptyState, {
                            icon: /*#__PURE__*/ _jsx(Folder, {}),
                            title: "No document libraries found",
                            description: "This site doesn't have any document libraries.",
                            size: "sm"
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
                            children: drives.map((drive)=>/*#__PURE__*/ _jsx("div", {
                                    onClick: ()=>handleDriveClick(drive),
                                    className: "bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-blue-300 cursor-pointer transition-all",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsx(Folder, {
                                                className: "w-8 h-8 text-green-500 flex-shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-0",
                                                children: [
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        className: "text-sm font-medium text-gray-900 truncate",
                                                        children: drive.name
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-xs text-gray-500",
                                                        children: drive.drive_type
                                                    }),
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        className: "text-xs text-gray-400 mt-1",
                                                        children: [
                                                            drive.item_count,
                                                            " ",
                                                            drive.item_count === 1 ? 'item' : 'items',
                                                            drive.used_size > 0 && ` · ${formatFileSize(drive.used_size)} used`
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                }, drive.id))
                        })
                    }) : /* Files and Folders List */ /*#__PURE__*/ _jsx("div", {
                        children: loading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center py-12 text-gray-500",
                            children: "Loading items..."
                        }) : items.length === 0 ? /*#__PURE__*/ _jsx(PageEmptyState, {
                            icon: /*#__PURE__*/ _jsx(FileText, {}),
                            title: "This folder is empty",
                            description: "No files or folders found here.",
                            size: "sm"
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "overflow-x-auto",
                                children: /*#__PURE__*/ _jsxs("table", {
                                    className: "min-w-full divide-y divide-gray-200",
                                    children: [
                                        /*#__PURE__*/ _jsx("thead", {
                                            className: "bg-gray-50",
                                            children: /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                        children: "Name"
                                                    }),
                                                    /*#__PURE__*/ _jsx("th", {
                                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                        children: "Modified"
                                                    }),
                                                    /*#__PURE__*/ _jsx("th", {
                                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                        children: "Size"
                                                    }),
                                                    /*#__PURE__*/ _jsx("th", {
                                                        className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                        children: "Actions"
                                                    })
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("tbody", {
                                            className: "bg-white divide-y divide-gray-200",
                                            children: [
                                                ...items
                                            ].sort((a, b)=>{
                                                if (a.is_folder && !b.is_folder) return -1;
                                                if (!a.is_folder && b.is_folder) return 1;
                                                return a.name.localeCompare(b.name);
                                            }).map((item)=>/*#__PURE__*/ _jsxs("tr", {
                                                    className: `hover:bg-gray-50 ${item.is_folder ? 'cursor-pointer' : ''}`,
                                                    onClick: item.is_folder ? ()=>handleFolderClick(item) : undefined,
                                                    children: [
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "px-6 py-4 whitespace-nowrap",
                                                            children: /*#__PURE__*/ _jsxs("div", {
                                                                className: "flex items-center",
                                                                children: [
                                                                    item.is_folder ? /*#__PURE__*/ _jsx(Folder, {
                                                                        className: "w-5 h-5 text-yellow-500"
                                                                    }) : /*#__PURE__*/ _jsx(FileText, {
                                                                        className: "w-5 h-5 text-blue-500"
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        className: "ml-3",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("div", {
                                                                                className: "text-sm font-medium text-gray-900",
                                                                                children: item.name
                                                                            }),
                                                                            item.is_folder && item.child_count > 0 && /*#__PURE__*/ _jsxs("div", {
                                                                                className: "text-xs text-gray-500",
                                                                                children: [
                                                                                    item.child_count,
                                                                                    " items"
                                                                                ]
                                                                            })
                                                                        ]
                                                                    })
                                                                ]
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsxs("td", {
                                                            className: "px-6 py-4 whitespace-nowrap",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("div", {
                                                                    className: "text-sm text-gray-900",
                                                                    children: formatDate(item.modified_at)
                                                                }),
                                                                /*#__PURE__*/ _jsxs("div", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: [
                                                                        "by ",
                                                                        item.last_modified_by
                                                                    ]
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500",
                                                            children: item.is_folder ? '--' : formatFileSize(item.size)
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: "px-6 py-4 whitespace-nowrap text-right text-sm font-medium",
                                                            children: /*#__PURE__*/ _jsxs("div", {
                                                                className: "flex items-center justify-end space-x-2",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx("button", {
                                                                        onClick: (e)=>{
                                                                            e.stopPropagation();
                                                                            window.open(item.web_url, '_blank');
                                                                        },
                                                                        className: "text-indigo-600 hover:text-indigo-900 p-1",
                                                                        title: "Open in SharePoint",
                                                                        children: /*#__PURE__*/ _jsx(ExternalLink, {
                                                                            className: "w-4 h-4"
                                                                        })
                                                                    }),
                                                                    !item.is_folder && /*#__PURE__*/ _jsx("button", {
                                                                        onClick: (e)=>{
                                                                            e.stopPropagation();
                                                                            handleDownload(item);
                                                                        },
                                                                        className: "text-gray-600 hover:text-gray-900 p-1",
                                                                        title: "Download",
                                                                        children: /*#__PURE__*/ _jsx(Download, {
                                                                            className: "w-4 h-4"
                                                                        })
                                                                    })
                                                                ]
                                                            })
                                                        })
                                                    ]
                                                }, item.id))
                                        })
                                    ]
                                })
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex-shrink-0",
                                    children: /*#__PURE__*/ _jsx(FileText, {
                                        className: "w-5 h-5 text-blue-600"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "ml-3",
                                    children: [
                                        /*#__PURE__*/ _jsx("h4", {
                                            className: "text-sm font-medium text-blue-900",
                                            children: "ACL-Filtered Documents"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-blue-800 mt-1",
                                            children: "Documents are automatically filtered based on your SharePoint permissions. You only see documents you have access to in SharePoint."
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
